import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import quizApi from '../utils/quizApi';
import '../styles/Quiz.css';

/**
 * Sitting a quiz — one question at a time.
 *
 * Every answer is saved to the server as it is picked, not batched at Submit. A
 * quiz is somebody's compliance record; losing ten answers to a closed tab and
 * making them start again (burning an attempt) is not a trade worth making for a
 * few fewer requests.
 *
 * The payload this screen receives carries NO answer key — the server strips it.
 * Marking happens server-side on Submit, so nothing here can reveal or influence
 * the result.
 */
function TakeQuiz() {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await quizApi.getAttempt(attemptId);
      setState(d);
      setAnswers(d.answers || {});
      // Resume where they left off: the first unanswered question, not question 1.
      const firstUnanswered = (d.questions || [])
        .findIndex((q) => !(d.answers || {})[q.id]?.length);
      setIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { load(); }, [load]);

  const questions = state?.questions || [];
  const quiz = state?.quiz;
  // The other half of the to-do. A quiz always has a task and a document behind it
  // (that is what `sit_gate` guaranteed before this attempt could be opened), so
  // both are reachable from here — going back to re-read the SOP mid-quiz should
  // not mean hunting for it in Orcanos.
  const task = state?.task || {};
  const current = questions[index];
  const picked = (current && answers[current.id]) || [];
  const answeredCount = questions.filter((q) => (answers[q.id] || []).length).length;

  const pick = async (optionId) => {
    if (!current || saving) return;
    const next = current.kind === 'multi'
      ? (picked.includes(optionId)
        ? picked.filter((id) => id !== optionId)
        : [...picked, optionId])
      : [optionId];

    setAnswers((a) => ({ ...a, [current.id]: next }));
    setError('');
    try {
      await quizApi.answer(attemptId, current.id, next);
    } catch (e) {
      // Put the UI back to what the server actually holds rather than leaving a
      // selection on screen that was never stored.
      setError(e.message);
      setAnswers((a) => ({ ...a, [current.id]: picked }));
    }
  };

  const submit = async () => {
    const unanswered = questions.length - answeredCount;
    if (unanswered > 0 && !window.confirm(
      `${unanswered} question${unanswered === 1 ? '' : 's'} still unanswered.\n\n`
      + 'Unanswered questions are marked wrong. Submit anyway?'
    )) return;

    setSaving(true);
    setError('');
    try {
      setResult(await quizApi.submit(attemptId));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="qz-page"><div className="qz-loading">Loading…</div></div>;

  if (error && !state) {
    return (
      <div className="qz-page">
        <div className="qz-empty">
          <b>This quiz isn’t available</b>
          <span>{error}</span>
          <button className="qz-btn qz-btn-ghost" onClick={() => navigate('/training/my')}>
            Back to my to-do
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- result */
  if (result) {
    return (
      <div className="qz-page">
        <div className="qz-take">
          <div className={`qz-result ${result.passed ? 'pass' : 'fail'}`}>
            <div className="qz-result-score">{result.score_pct}%</div>
            <div className="qz-result-verdict">
              {result.passed ? 'Passed' : 'Not passed'}
            </div>
            <div className="qz-result-sub">
              {result.correct} of {result.total} correct · pass mark {result.pass_mark}%
              {!result.passed && (
                <> · {result.attempts_remaining} attempt
                  {result.attempts_remaining === 1 ? '' : 's'} left</>
              )}
            </div>
          </div>

          {/* Passing and being signed in Orcanos are two different facts. Saying so
              plainly is the point — a pass that hasn't become a record must never
              read as one that has. */}
          {result.passed && result.record_state === 'pending' && (
            <div className="qz-note qz-note-info">
              <span>⏳</span>
              <div>
                <b>Your pass is recorded.</b> The training record is still being written to
                Orcanos — until it lands, your task there is still open. You don’t need to
                do anything; this page will show it as signed once it’s through.
              </div>
            </div>
          )}

          {!result.passed && result.attempts_remaining > 0 && (
            <div className="qz-note qz-note-warn">
              <span>↻</span>
              <div>Review the document and try again when you’re ready.</div>
            </div>
          )}

          {!result.passed && result.attempts_remaining === 0 && (
            <div className="qz-note qz-note-err">
              <span>⚠</span>
              <div>
                <b>You’ve used every attempt.</b> Contact your training manager — they can
                review this with you.
              </div>
            </div>
          )}

          <div className="qz-take-foot">
            {task.url && (
              <a className="qz-btn qz-btn-ghost" href={task.url}
                 target="_blank" rel="noopener noreferrer">
                Training task ↗
              </a>
            )}
            <span className="qz-spacer" />
            <button className="qz-btn qz-btn-primary" onClick={() => navigate('/training/my')}>
              Back to my to-do
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ----------------------------------------------------------------- sitting */
  return (
    <div className="qz-page">
      <div className="qz-take">
        <div className="qz-head">
          <div>
            <h2>{quiz?.doc_name || quiz?.title || 'Quiz'}</h2>
            <p className="qz-sub">
              <span className="qz-key">{quiz?.doc_key}</span>
              {quiz?.revision ? <> · revision {quiz.revision}</> : null}
              {' · '}pass mark {quiz?.pass_mark}%
              {' · '}attempt {state?.attempt?.attempt_no} of {quiz?.max_attempts}
            </p>
          </div>
          <div className="qz-head-actions">
            {task.doc_url && (
              <a className="qz-btn qz-btn-ghost qz-btn-sm" href={task.doc_url}
                 target="_blank" rel="noopener noreferrer">
                Document ↗
              </a>
            )}
            {task.url && (
              <a className="qz-btn qz-btn-ghost qz-btn-sm" href={task.url}
                 target="_blank" rel="noopener noreferrer"
                 title={task.key ? `Training task ${task.key}` : 'Your training task'}>
                Training task ↗
              </a>
            )}
            <button className="qz-btn qz-btn-ghost qz-btn-sm" onClick={() => navigate('/training/my')}>
              Save &amp; exit
            </button>
          </div>
        </div>

        {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}

        <div className="qz-progress">
          <span className="qz-progress-lab">Question {index + 1} of {questions.length}</span>
          <div className="qz-bar">
            <span style={{ width: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%` }} />
          </div>
          <span className="qz-progress-lab">{answeredCount} answered</span>
        </div>

        {current ? (
          <>
            <p className="qz-question">{current.text}</p>
            {current.kind === 'multi' && (
              <p className="qz-sub" style={{ marginTop: -8, marginBottom: 12 }}>
                Choose every answer that applies — a partly correct answer is marked wrong.
              </p>
            )}

            {current.options.map((o) => (
              <button
                key={o.id}
                type="button"
                className={`qz-choice${picked.includes(o.id) ? ' picked' : ''}`}
                onClick={() => pick(o.id)}
              >
                <span className={`qz-dot${current.kind === 'multi' ? ' square' : ''}`} />
                {o.text}
              </button>
            ))}

            <div className="qz-take-foot">
              <button
                className="qz-btn qz-btn-ghost"
                disabled={index === 0}
                onClick={() => setIndex((i) => i - 1)}
              >← Back</button>
              <span className="qz-spacer" />
              {index < questions.length - 1 ? (
                <button className="qz-btn qz-btn-primary" onClick={() => setIndex((i) => i + 1)}>
                  Next →
                </button>
              ) : (
                <button className="qz-btn qz-btn-primary" onClick={submit} disabled={saving}>
                  {saving ? 'Submitting…' : 'Submit'}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="qz-empty">
            <b>This quiz has no questions</b>
            <span>Tell your training manager — it shouldn’t have been published.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TakeQuiz;
